import axios from "axios";
import LoggerService from "./LoggerService";

const API_BASE_URL = process.env.REACT_APP_API_HOSTNAME;
const MAX_RETRIES = 3;
const BASE_DELAY = 1000; // 1 second

class ApiService {
  constructor(getAccessToken, onTokenExpired) {
    this.getAccessToken = getAccessToken;
    this.onTokenExpired = onTokenExpired;
    
    // Create an instance-specific axios client to avoid stacking global interceptors
    this.axiosInstance = axios.create();
    
    // Track interceptor IDs for cleanup
    this.responseInterceptorId = null;
    
    this.setupInterceptors();
  }

  /**
   * Setup axios interceptors for instance-specific error handling
   * @private
   */
  setupInterceptors() {
    // Response interceptor for handling 401 errors on this instance
    this.responseInterceptorId = this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;

        // Handle 401 - token expired
        if (status === 401) {
          LoggerService.addBreadcrumb(
            "Token expired - signing out",
            "auth",
            "warning"
          );

          // Call token expiration handler
          this.onTokenExpired?.();
        }

        // Always reject the error to be handled by retry logic
        return Promise.reject(error);
      }
    );
  }

  /**
   * Cleanup interceptors when service is destroyed
   */
  cleanup() {
    if (this.responseInterceptorId !== null) {
      this.axiosInstance.interceptors.response.eject(this.responseInterceptorId);
      this.responseInterceptorId = null;
    }
  }

  /**
   * Make an API request with exponential backoff retry logic
   * @param {string} method - HTTP method (GET, POST, etc.)
   * @param {string} url - API endpoint (relative to API_BASE_URL)
   * @param {object} data - Request body (for POST/PUT)
   * @param {object} customConfig - Additional axios config
   * @returns {Promise} Response data
   */
  async request(method, url, data = null, customConfig = {}) {
    const accessToken = this.getAccessToken?.();

    if (!accessToken) {
      LoggerService.sendEvent("api_request_no_token", "warning", {
        message: "Access token missing",
        endpoint: url,
      });
      throw new Error("Access token is missing");
    }

    const fullUrl = `${API_BASE_URL}${url}`;
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "*/*",
        ...customConfig.headers,
      },
      ...customConfig,
    };

    return this.retryWithBackoff(
      async () => {
        const response = await this.axiosInstance({
          method,
          url: fullUrl,
          data,
          ...config,
        });
        return response.data;
      },
      url
    );
  }

  /**
   * Retry logic with exponential backoff
   * @private
   */
  async retryWithBackoff(requestFn, endpoint, attempt = 0) {
    try {
      return await requestFn();
    } catch (error) {
      const status = error.response?.status;

      // Handle 401 - token expired (interceptor already called onTokenExpired)
      if (status === 401) {
        LoggerService.addBreadcrumb(
          `Token expired at ${endpoint}`,
          "auth",
          "warning"
        );
        return null; // Return null instead of throwing
      }

      // Determine if we should retry
      const isServerError = status >= 500;
      const isNetworkError = !error.response;
      const shouldRetry = (isServerError || isNetworkError) && attempt < MAX_RETRIES;

      if (shouldRetry) {
        const delay = BASE_DELAY * Math.pow(2, attempt);
        LoggerService.sendEvent("api_request_retry", "warning", {
          attempt: attempt + 1,
          maxRetries: MAX_RETRIES,
          delay,
          endpoint,
          status: status || "no response",
          error: error.message,
        });

        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.retryWithBackoff(requestFn, endpoint, attempt + 1);
      }

      // Log final error
      LoggerService.sendEvent("api_request_error", "error", {
        endpoint,
        status: status || "no response",
        attempt,
        maxRetries: MAX_RETRIES,
        error: error.message,
      });

      throw error;
    }
  }

  /**
   * GET request
   */
  get(url, config) {
    return this.request("GET", url, null, config);
  }

  /**
   * POST request
   */
  post(url, data, config) {
    return this.request("POST", url, data, config);
  }

  /**
   * PUT request
   */
  put(url, data, config) {
    return this.request("PUT", url, data, config);
  }

  /**
   * DELETE request
   */
  delete(url, config) {
    return this.request("DELETE", url, null, config);
  }

  /**
   * Fetch user profile data
   */
  async getUserProfile() {
    try {
      const data = await this.get("/v1/account/profile");
      const defaultClient = data?.partners?.[0]?.name || "";

      LoggerService.addBreadcrumb(
        `User profile loaded with ${data?.partners?.length || 0} partner(s)`,
        "user",
        "info"
      );

      return { ...data, defaultClient };
    } catch (error) {
      LoggerService.sendEvent("api_user_profile_fetch_error", "error", {
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Fetch content from external URL with authorization
   */
  async fetchExternalUrl(url) {
    const accessToken = this.getAccessToken?.();

    if (!accessToken) {
      LoggerService.sendEvent("external_url_no_token", "warning", {
        message: "Access token missing",
        url,
      });
      throw new Error("Access token is missing");
    }

    return this.retryWithBackoff(
      async () => {
        const response = await this.axiosInstance.get(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "*/*",
          },
        });
        return response.data;
      },
      url
    );
  }
}

export default ApiService;
